import React, { useEffect, useState, useMemo } from "react";
import { Avatar, Box, Grid, List, ListItem, ListItemAvatar, ListItemSecondaryAction, ListItemText, Paper, Typography } from '@mui/material';
import { getAllSites } from "../../../../selectors/AdminSelectors";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router-dom";
import { StyledAutocomplete, StyledTextField } from "../../../common/StyledTextField";
import { ArrowBackIos, ArrowForwardIos, Check, DescriptionOutlined, Settings, StoreOutlined, StorefrontOutlined, Warning, WarningAmber } from "@mui/icons-material";
import { TooltipIconButton } from "../../../common/StyledButtons";
import Colors from "../../../../constants/Colors";
import StyledSubHeader from "../../../common/StyledSubHeader";
import StyledPage from "../../../common/StyledPage";
import StyledSection from "../../../common/StyledSection";
import { getSupportSummariesForSiteForAdmin } from "../../../../actions/internal/AdminActions";
import { formatDateRangeFancy, formatDateToFancyString } from "../../../../utilities/DateUtilities";
import StyledProgressCircle from "../../../common/StyledProgressCircle";

const PAGE_LIMIT = 10;

const AdminSupportSummariesPage = ({ history, location }) => {
    const [allSites] = useState(getAllSites() || []);
    const [selectedSiteId, setSelectedSiteId] = useState();
    const [isLoadingSummary, setIsLoadingSummary] = useState(false);
    const [summaryData, setSummaryData] = useState();
    const [error, setError] = useState();

    const siteOptions = Array.isArray(allSites)
        ? allSites.map((a) => ({
            key: a.site_id,
            label: a.site_details.site_name,
            description: a.site_details.site_name,
        }))
        : [];

    const selectedSiteOption = useMemo(() => {
        if (!selectedSiteId) return null;

        const foundOption = siteOptions.find((option) => option.key === selectedSiteId);
        return foundOption || null;
    }, [siteOptions, selectedSiteId]);

    const handleGetSupportSummaries = async (site_id) => {
        try {
            setError();
            setSelectedSiteId(site_id);
            setSummaryData();

            const searchParams = new URLSearchParams(location.search);

            if (site_id) {
                searchParams.set("site_id", site_id);
            } else {
                searchParams.delete("site_id");
            }

            history.replace({
                pathname: location.pathname,
                search: searchParams.toString() ? `?${searchParams.toString()}` : "",
            });

            if (!site_id) return;

            setIsLoadingSummary(true);
            const summaries = await getSupportSummariesForSiteForAdmin(site_id);
            setSummaryData(summaries);
        } catch (ex) {
            setError('Could not load summary at this time');
        } finally {
            setIsLoadingSummary(false);
        }
    };

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const siteIdFromUrl = searchParams.get("site_id");

        if (!siteIdFromUrl) return;

        const matchingSite = siteOptions.find((option) => String(option.key) === String(siteIdFromUrl));

        if (matchingSite) {
            handleGetSupportSummaries(matchingSite.key);
        }
    }, []);

    return (
        <StyledPage>
            <StyledSubHeader title="Support Summaries" />
            <StyledSection title="View Summaries">
                <StyledAutocomplete
                    options={siteOptions}
                    label="Business"
                    getOptionLabel={(option) => option.label}
                    value={selectedSiteOption}
                    isOptionEqualToValue={(option, value) => option.key === value.key}
                    renderOption={(props, option) => (
                        <li {...props}>
                            <Box>
                                <Typography sx={{ fontWeight: 600 }}>{option.label}</Typography>
                            </Box>
                        </li>
                    )}
                    onChange={(_, newValue) => {
                        handleGetSupportSummaries(newValue ? newValue.key : null);
                    }}
                    renderInput={(params) => (
                        <StyledTextField {...params} label="Business" />
                    )}
                />
                <Box>
                    {!selectedSiteId && !isLoadingSummary && (
                        <p style={{ margin: 0, textAlign: 'center', padding: '20px 0px' }}>
                            Please select a business before viewing summaries.
                        </p>
                    )}

                    {isLoadingSummary && (
                        <StyledProgressCircle />
                    )}

                    {selectedSiteId && !isLoadingSummary && (!summaryData || summaryData.length === 0) && (
                        <p style={{ margin: 0, textAlign: 'center', padding: '20px 0px' }}>
                            No summaries were found for this business.
                        </p>
                    )}

                    {selectedSiteId && !isLoadingSummary && summaryData && summaryData.length > 0 && (
                        <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px', borderRadius: '8px' }}>
                            <List style={{ flex: 1 }}>
                                {summaryData && summaryData.map((summary, index) => (
                                    <ListItem
                                        dense
                                        key={summary.summary_id}
                                        sx={{
                                            backgroundColor: '#fff',
                                            marginBottom: '5px',
                                            borderBottom: index !== summaryData.length - 1 ? '1px solid #e0e0e0' : 'none',
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                sx={{
                                                    bgcolor: summary.summary_reviewed_epoch ? Colors.primaryColor : Colors.warningBackground
                                                }}
                                            >
                                                {summary.summary_reviewed_epoch ? <Check /> : <Warning />}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={formatDateRangeFancy(summary.summary_time_period.start, summary.summary_time_period.end)}
                                            secondary={summary.summary_reviewed_epoch ? `Reviewed on ${formatDateToFancyString(summary.summary_reviewed_epoch)}` : 'Needs Review'}
                                        />
                                        <ListItemSecondaryAction>
                                            <TooltipIconButton
                                                text={'View Summary'}
                                                icon={<DescriptionOutlined sx={{ color: Colors.primaryColor }} />}
                                                onClick={() => history.push(`/admin/support-summaries/${selectedSiteId}/${summary.summary_id}`)}
                                            />
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Box>
                

            </StyledSection>
        </StyledPage>
    );
};

export default withRouter(observer(AdminSupportSummariesPage));